import { useAuth, useUser } from "@clerk/clerk-react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useSubscription } from "@/hooks/useSubscription";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute — checks Clerk auth, then subscription status.
 * NOT loaded     → spinner (Clerk still initialising)
 * not signed in  → /sign-in  (redirect immediately, don't wait for sub)
 * sub loading    → spinner (user is signed in, waiting for sub data)
 * expired        → /subscribe
 * trial / active → allow
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const { status: subStatus } = useSubscription();
  const { status: onboardStatus } = useOnboardingStatus();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // ── Stripe row backfill (defense-in-depth against webhook signature drift) ──
  // Two trigger paths:
  //   (1) ?subscribed=true in URL — just finished Stripe Checkout, write the
  //       row eagerly so useSubscription finds it on the immediate read.
  //   (2) On any sign-in where useSubscription returns 'none' for a user we
  //       haven't already checked this session — pull their Stripe state
  //       directly via verify-checkout. LJ scenario: paid in a previous
  //       session, webhook didn't write the local row, re-login was bouncing
  //       to /subscribe instead of resuming at the outstanding step.
  // A per-session sessionStorage flag prevents repeated calls for users who
  // genuinely have no Stripe subscription.
  const verifyingRef = useRef(false);
  const externalId = user?.externalId ?? null;
  const sessionFlagKey = externalId ? `tm-verify-checkout-tried:${externalId}` : null;
  const alreadyTried =
    sessionFlagKey && typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(sessionFlagKey) === '1'
      : false;
  const subscribedQuery = searchParams.get('subscribed') === 'true';
  const [verified, setVerified] = useState<'pending' | 'done' | 'skip'>(
    subscribedQuery ? 'pending' : 'skip',
  );
  // Promote 'skip' → 'pending' when useSubscription reports 'none' for a user
  // we haven't tried yet — covers the cold sign-in path.
  useEffect(() => {
    if (verified !== 'skip') return;
    if (!isLoaded || !isSignedIn || !externalId) return;
    if (subStatus !== 'none') return;
    if (alreadyTried) return;
    setVerified('pending');
  }, [verified, isLoaded, isSignedIn, externalId, subStatus, alreadyTried]);

  useEffect(() => {
    if (verified !== 'pending') return;
    if (!isLoaded || !isSignedIn || !externalId) return;
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    (async () => {
      let bodyOk: any = null;
      try {
        const jwt = await getToken({ template: 'supabase', skipCache: true });
        if (jwt) {
          const res = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-checkout`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${jwt}`,
                apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
                'Content-Type': 'application/json',
              },
            },
          );
          bodyOk = await res.json().catch(() => null);
        }
      } catch (e) {
        console.warn('[ProtectedRoute] verify-checkout failed:', e);
      } finally {
        // Always set the per-user session flag so we don't re-ping Stripe on
        // every page navigation. If verify-checkout DID succeed, the next
        // page render reads the fresh row; if it returned no_customer /
        // no_subscription, we accept the 'none' status and let the redirect
        // to /subscribe stand.
        if (sessionFlagKey) sessionStorage.setItem(sessionFlagKey, '1');
        if (bodyOk?.ok) {
          // Backfill succeeded — hard-reload so useSubscription re-fetches.
          // Strip ONLY the subscribed marker; preserve returnTo etc so
          // downstream pages (like /verify-identity → /onboarding) keep
          // their context.
          const url = new URL(window.location.href);
          url.searchParams.delete('subscribed');
          window.location.replace(url.pathname + url.search + url.hash);
        } else {
          // No subscription on Stripe side either — proceed with the
          // ProtectedRoute redirect chain (will land on /subscribe).
          setVerified('done');
        }
      }
    })();
  }, [verified, isLoaded, isSignedIn, externalId, getToken, sessionFlagKey]);

  // Design-preview bypass: when VITE_PREVIEW_NO_AUTH=true (set on the
  // tm-design-preview Vercel project only), all auth gates short-circuit so
  // a designer / external reviewer can see every page without a real account.
  // This flag is NEVER set on staging or production.
  if (import.meta.env.VITE_PREVIEW_NO_AUTH === "true") {
    return <>{children}</>;
  }

  // Block parent app routes on admin domain
  if (window.location.hostname.includes('admin')) {
    return <Navigate to="/admin" replace />;
  }

  // 1. Wait for Clerk to finish initialising
  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C9A96E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // 2. Not signed in → redirect immediately (don't wait for subscription)
  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  // 3. Wait for sub + onboarding signals before deciding redirects.
  //    Also wait for verify-checkout to finish when ?subscribed=true so we
  //    don't redirect to /subscribe before the Stripe-backfill writes the row.
  if (subStatus === "loading" || onboardStatus === "loading" || verified === 'pending') {
    return (
      <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C9A96E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // 4. NEW FLOW: No subscription row + not already on /subscribe → /subscribe.
  //    This forces Stripe Checkout (card-on-file trial) before onboarding.
  //    User can use TMTEST promo for a 100%-off trial start.
  if ((subStatus === "none" || subStatus === "expired") &&
      !location.pathname.startsWith("/subscribe")) {
    return <Navigate to="/subscribe" replace />;
  }

  // 5. Sub OK but onboarding incomplete → /onboarding.
  //    Exception: /verify-identity is the KYC prereq that runs BEFORE
  //    onboarding; redirecting it back to /onboarding creates an infinite
  //    ping-pong (Onboarding navigates to /verify-identity when kyc isn't
  //    approved → ProtectedRoute bounces back → spinner restarts at ~2Hz).
  if (
    onboardStatus === "incomplete" &&
    !location.pathname.startsWith("/onboarding") &&
    !location.pathname.startsWith("/verify-identity")
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}
